import {useEffect, useRef, useState} from 'react';
import {WdiModalAppProps, wdiCore, FieldProps} from '@/components/WdiControls';
import {ProFormInstance} from '@ant-design/pro-form';
import {WdiSelect, WdiSelectFormat, WdiTreeSelect} from '@/components/WdiControls/WdiSelect';
import {Form, Input, message, Tooltip} from 'antd';
import {WdiModalForm} from '@/components/WdiControls/WdiModalForm';
import {BO} from '@/services/mdm/bo';
import {ResponseStatus} from '@/utils/request';
import {SoldTo} from '@/services/mdm/soldto';
import {FormSa} from '@/services/sales/sa';
import {BudgetProject} from '@/services/sales/bp/budgetProject';
import {Enum} from '@/enums/system';
import {EC} from "@/services/mdm/ec";

const FreeGoodsPickerApp: React.FC<WdiModalAppProps> = (props) => {

  const formRef = useRef<ProFormInstance>();
  const [budgetProject, setBudgetProject] = useState<API.BudgetProject>();
  const [boOptions, setBoOptions] = useState<any[]>([]);

  const hasInitializedSoldToCode = useRef(false);

  const handleSoldToCodeChange = (value: any) => {
    formRef.current?.setFieldsValue({
      boId: null
    });
    if (value) {
      BO.search({ soldToCode: value, boLevels: `${Enum.BOLevel.BO4}`, withPermissions: true, isActive: true }).then(response => {
        setBoOptions(response?.data.map((item) => { return { value: item.boId, label: item.boName } }));
        if (response.data && response?.data.length == 1) {
          formRef.current?.setFieldsValue({
            boId: response.data[0].boId
          });
        }
      });
    } else {
      setBoOptions([]);
    }
  };

  useEffect(() => {
    if (props.data?.budgetProjectId) {
      BudgetProject.get(props.data.budgetProjectId).then(response => {
        setBudgetProject(response.data);
      })
    }
  }, [props.data.budgetProjectId]);

  useEffect(() => {
    if (props.data?.formSaDetail) {
      const detail = props.data?.formSaDetail;
      formRef.current?.setFieldsValue({
        formSaDetailId: detail.formSaDetailId,
        rowNumber: detail.rowNumber,
      });
      if (detail.soldToCode) {
        formRef.current?.setFieldsValue({ soldToCode: { value: detail.soldToCode, label: wdiCore.String.Join(detail.soldToCode, detail.soldToName) } });
        handleSoldToCodeChange(detail.soldToCode);
        formRef.current?.setFieldsValue({ boId: detail.boId });
      }
    }
  }, [props.data?.formSaDetail]);

  return (
    <WdiModalForm
      formRef={formRef}
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 12 }}
      labelAlign='left'
      layout="horizontal"
      title={props.title}
      visible={props.visible}
      modalProps={{
        onCancel: () => { props.onClose ? props.onClose() : null },
        destroyOnClose: true,
        maskClosable: false,
        okText: '下一步'
      }}
      onFinish={async (values) => {
        const { soldToCode,ecId, ...entity } = values;
        entity.soldToCode = WdiSelectFormat.getvalue(soldToCode);
        entity.ecId = WdiSelectFormat.getvalue(ecId);
        const response = await FormSa.Buttons.validatePicker({
          rowIndex: props.data.rowIndex,
          formSaDetail: {
            ecId: entity.ecId,
            soldToCode: entity.soldToCode,
            boId: entity.boId
          },
          formSa: {
            pageTypeId: props.data?.pageTypeId,
            budgetProjectId: props.data?.budgetProjectId,
          }
        });

        if (response.code !== ResponseStatus.SUCCESS) {
          message.error(response.message);
        } else {
          if (props.onSave) {
            props.onSave(response);
          }
        }
      }}
    >
      <Form.Item name="formSaDetailId" label="formSaDetailId" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="rowNumber" label="rowNumber" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="ecId" label="费用项" rules={[{ required: true, message: '费用项不能为空!' }]}>
        <WdiTreeSelect {...{
          placeholder: "请选择费用项",
          dataLoading: async (params) => {
            const response = await EC.searchEc3(Object.assign({
              budgetProjectId: props.data?.budgetProjectId,
              toParentLevel: Enum.ECLevel.EC2,
              isActive: true,
              pageTypeIds: String(Enum.PageType.FREE_GOODS_APPLICATION)
            }, params));

            const {treeData, dict} = FieldProps.TreeSelect.formatTreeData({
              textField: "ecName",
              valueField: "ecId",
              id: "ecId",
              pId: "parentId",
              level: "ecLevel",
              disabledLevel: [Enum.ECLevel.EC2],
            }, response.data, (node: any, item: any) => {
              node.title = (
                <Tooltip placement="topLeft" title={item.remark}><span>{node.title}</span></Tooltip>
              );
              return node;
            });

            let ecList = response.data.filter(item => item?.ecLevel == Enum.ECLevel.EC3);
            if (ecList.length == 1) {
              formRef.current?.setFieldsValue({
                ecId: ecList[0].ecId
              });
            }

            return { treeData, dict };
          },
          reloadKey: props.data?.budgetProjectId
        }} />

      </Form.Item>
      <Form.Item
        name="soldToCode"
        label="客户"
        rules={[{ required: true, message: '客户不能为空!' }]}>
        <WdiSelect {...{
          placeholder: "请选择客户",
          showSearch: true,
          remote: true,
          onChange: handleSoldToCodeChange,
          dataLoading: async (params) => {
            const response = await SoldTo.ajaxSearch(Object.assign({ withPermissions: true, isActive: true, budgetProjectId: props.data?.budgetProjectId, soldToCodes: budgetProject?.limitedSoldToCodes, }, params));
            const soldToOptions = response.data?.map((item) => { return { value: item.soldToCode, label: item.codeOrName } });
            if (!hasInitializedSoldToCode.current && soldToOptions && soldToOptions.length === 1) {
              hasInitializedSoldToCode.current = true;
              formRef.current?.setFieldsValue({ soldToCode: { value: soldToOptions[0].value, label: soldToOptions[0].label } });
              handleSoldToCodeChange(soldToOptions[0].value);
            }
            return soldToOptions;
          },
          reloadKey: budgetProject?.limitedSoldToCodes
        }} />
      </Form.Item>
      <Form.Item
        name="boId"
        label="四级使用部门"
        rules={[{ required: true, message: '四级使用部门不能为空!' }]}>
        <WdiSelect {...{
          placeholder: "请选择四级使用部门",
          showSearch: true,
          options: boOptions
        }} />
      </Form.Item>
    </WdiModalForm>
  );
}
export default FreeGoodsPickerApp;
